import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import {
  CalendarIcon,
  ChartLineIcon,
  FileIcon,
  LinkIcon,
  MegaphoneIcon,
  MessagesSquareIcon,
  RssIcon,
  SettingsIcon,
  TargetIcon,
  UsersIcon,
} from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";

import api from "api";

import { getSidebarIcon, SidebarIconName } from "../components/SidebarIcons";
import { IconData } from "../ui/icon";
import { useCurrentUser } from "./useCurrentUser";

export type GroupSidebarEntry = Omit<
  NonNullable<OpenAPI.Schemas["Group"]["sidebar"]>[number],
  "icon"
> & {
  icon?: IconData;
  iconName?: SidebarIconName;
  visibleUserCount?: number;
  visibleRoleNames?: string[];
  sidebarCount?: number;
  sidebarCountWarning?: number;
};

type GroupSidebar = GroupSidebarEntry[];

const sidebarItems: GroupSidebar = [
  {
    id: "activityFeed",
    translate: "generic.activityFeed",
    icon: RssIcon,
    location: "feed",
  },
  {
    id: "challenges",
    translate: "generic.challenges",
    icon: TargetIcon,
    location: "challenges",
  },
  {
    id: "members",
    translate: "generic.members",
    icon: UsersIcon,
    location: "members",
    permissions: [
      "group.listUsers",
      "group.listBusinesses",
      "group.editSettings",
    ],
  },
  {
    id: "announcements",
    translate: "generic.announcements",
    icon: MegaphoneIcon,
    location: "announcements",
    permissions: ["group.manageAnnouncements", "group.viewAnnouncementLogs"],
  },
  {
    id: "activity",
    translate: "generic.activity",
    icon: ChartLineIcon,
    location: "activity",
    permissions: ["group.editSettings", "group.viewActivity"],
  },
  {
    id: "settings",
    translate: "generic.settings",
    icon: SettingsIcon,
    location: "settings",
    permissions: [
      "group.editSettings",
      "group.managePages",
      "group.manageDiscussions",
      "group.manageCalendars",
    ],
  },
];

const sidebarItemsById = sidebarItems.reduce(
  (acc, item) => {
    acc[item.id] = item;
    return acc;
  },
  {} as Record<string, (typeof sidebarItems)[number]>,
);

export const useGroupSidebar = (group: OpenAPI.Schemas["Group"]) => {
  const user = useCurrentUser(api);
  const groupId = group?._id;
  const groupHasCustomSidebar = !!group?.sidebar?.length;
  const canManageGroups = hasPermission(
    user,
    "org.manageGroups",
    group?.organisation,
  );
  const canEditSidebar =
    canManageGroups || hasPermission(user, "group.editSettings", groupId);
  const canManagePages = hasPermission(user, "group.managePages", groupId);

  // Sidebar editors need the full page list (with visibility counts), not only pinned.
  const pageQuery =
    canEditSidebar || canManagePages ? api.pages.getAll : api.pages.getPinned;
  const pagesQuery = useQuery(
    initUseQuery(pageQuery, ["group", groupId], { isEnabled: !!groupId }),
  );

  const discussionsQuery = useQuery(
    initUseQuery(api.discussions.getAll, ["group", groupId], {
      isEnabled: !!groupId,
    }),
  );

  const calendarsQuery = useQuery(
    initUseQuery(api.calendars.getAll, ["group", groupId], {
      isEnabled: !!groupId,
    }),
  );

  const allItems = useMemo(
    () =>
      sidebarItems
        .concat(
          (pagesQuery.data?.pages ?? []).map((page) => ({
            id: page._id,
            title: page.title,
            type: "page",
            icon: FileIcon,
            location: `custom-pages/${page._id}`,
            visibleUserCount: page.visibleUserCount,
            visibleRoleNames: page.visibleRoleNames,
          })),
        )
        .concat(
          (discussionsQuery.data?.discussions ?? []).map((discussion) => ({
            id: discussion._id,
            title: discussion.title,
            type: "discussion",
            icon: MessagesSquareIcon,
            location: `discussions/${discussion._id}`,
            sidebarCount: (discussion as { unseenCommentCount?: number })
              .unseenCommentCount,
            sidebarCountWarning: (
              discussion as { requiresApprovalCommentCount?: number }
            ).requiresApprovalCommentCount,
            visibleUserCount: discussion.visibleUserCount,
            visibleRoleNames: discussion.visibleRoleNames,
          })),
        )
        .concat(
          (calendarsQuery.data?.calendars ?? []).map((cal) => ({
            id: cal._id,
            title: cal.title,
            type: "calendar",
            icon: CalendarIcon,
            location: `calendars/${cal._id}`,
            visibleUserCount: cal.visibleUserCount,
            visibleRoleNames: cal.visibleRoleNames,
          })),
        )
        .concat(
          (group?.links || []).map((link) => ({
            ...link,
            location: link.location,
            id: link.id,
            icon: LinkIcon,
            type: "link",
          })),
        ),
    [pagesQuery.data, group?.links, discussionsQuery.data, calendarsQuery.data],
  );

  const entries = useMemo(() => {
    const withActivityFeedCount = (
      entry: GroupSidebarEntry | undefined,
    ): GroupSidebarEntry | undefined => {
      if (!entry) {
        return entry;
      }
      if (entry.id === "activityFeed") {
        const count = group?.unseenActivityFeedCount;
        return { ...entry, sidebarCount: count || undefined };
      }
      return entry;
    };

    const groupSidebar = groupHasCustomSidebar
      ? (
          group?.sidebar?.map((sidebarItem) => {
            if (
              sidebarItem.type === "discussion" ||
              sidebarItem.type === "calendar" ||
              sidebarItem.type === "page" ||
              sidebarItem.type === "link"
            ) {
              const currentItem = allItems.find(
                (item) => item.id === sidebarItem.id,
              );
              if (!currentItem) {
                return currentItem;
              }
              return {
                ...currentItem,
                iconName: sidebarItem.icon,
                icon: getSidebarIcon(sidebarItem.icon) ?? currentItem.icon,
              };
            }

            return withActivityFeedCount(sidebarItemsById[sidebarItem.id]);
          }) ?? []
        ).filter((item) => !!item)
      : sidebarItems.map((item) => withActivityFeedCount(item)!);
    if (canManageGroups) {
      return groupSidebar.filter((entry) => !!entry);
    }
    return groupSidebar.filter((entry) => {
      if (!entry) {
        return false;
      }
      if (!entry.permissions) {
        return true;
      }
      return !!entry.permissions.find((permission) =>
        hasPermission(
          user,
          permission as NonNullable<
            OpenAPI.Schemas["User"]["permissions"]
          >[string][number],
          groupId,
        ),
      );
    });
  }, [user, canManageGroups, group, allItems, groupHasCustomSidebar, groupId]);

  return {
    groupId: group?._id,
    isLoading:
      group &&
      groupHasCustomSidebar &&
      (pagesQuery.isLoading || calendarsQuery.isLoading),
    entries,
    allItems,
  };
};
